import { useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, TextField, Select, Checkbox, Button, Badge, Box, Collapsible, Divider, Tooltip } from '@shopify/polaris';
import { DeleteIcon, DuplicateIcon, PlusIcon } from '@shopify/polaris-icons';
import { IO_LABELS, STARTER_OPTIONS, GROUP_LABELS, formatEur, formatNumber, newMotor } from './shared.js';

const MOTOR_GRID = 'minmax(110px, 2fr) minmax(72px, 0.8fr) minmax(130px, 1.4fr) minmax(56px, 0.6fr) 32px';

const KIND_OPTIONS = [
  { label: 'Floor-standing', value: 'floor' },
  { label: 'Wall-mounted', value: 'wall' }
];

function MotorRow({ motor, onChange, onRemove }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: MOTOR_GRID, gap: 8, alignItems: 'center' }}>
      <TextField labelHidden label="Motor" value={motor.name} placeholder="Description" onChange={(value) => onChange({ ...motor, name: value })} autoComplete="off" />
      <TextField labelHidden label="Power (kW)" value={motor.kw} type="number" min={0} step={0.01} onChange={(value) => onChange({ ...motor, kw: value })} autoComplete="off" />
      <Select labelHidden label="Starter" options={STARTER_OPTIONS} value={motor.starter} onChange={(value) => onChange({ ...motor, starter: value })} />
      <TextField labelHidden label="Qty" value={motor.qty} type="number" min={1} onChange={(value) => onChange({ ...motor, qty: value })} autoComplete="off" />
      <Button icon={DeleteIcon} variant="tertiary" accessibilityLabel="Remove motor" onClick={onRemove} />
    </div>
  );
}

function ComparableList({ comparables }) {
  if (!comparables?.length) return null;
  return (
    <BlockStack gap="100">
      <Text as="p" variant="bodySm" tone="subdued">
        Most similar cabinets Elektro Tech priced
      </Text>
      {comparables.map((c, i) => (
        <InlineStack key={`${c.sourceId}-${i}`} align="space-between" blockAlign="center" wrap={false} gap="200">
          <Box minWidth="0">
            <Text as="p" variant="bodySm" truncate>
              <Tooltip content={c.summary || c.title}>
                <span>
                  {c.name} · {c.title}
                </span>
              </Tooltip>
            </Text>
          </Box>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <Text as="span" variant="bodySm" tone="subdued">
              {c.date ? String(c.date).slice(0, 7) : ''}
            </Text>
            <Text as="span" variant="bodySm" fontWeight="semibold">
              {formatEur(c.priceToday)}
            </Text>
            <Badge tone={c.similarityPct >= 85 ? 'success' : c.similarityPct >= 65 ? 'attention' : undefined}>{`${c.similarityPct}%`}</Badge>
          </InlineStack>
        </InlineStack>
      ))}
    </BlockStack>
  );
}

export default function CabinetCard({ cabinet, index, result, onChange, onDuplicate, onRemove, canRemove }) {
  const [showLines, setShowLines] = useState(false);
  const resolved = result?.resolved;
  const set = (patch) => onChange({ ...cabinet, ...patch });
  const setIo = (type, value) => set({ io: { ...cabinet.io, [type]: value } });
  const setMotor = (key, motor) => set({ motors: cabinet.motors.map((m) => (m.key === key ? motor : m)) });
  const addMotor = (starter) => set({ motors: [...cabinet.motors, newMotor(starter)] });
  const motorCount = cabinet.motors.reduce((sum, m) => sum + (Number(m.qty) || 1), 0);
  const motorKw = cabinet.motors.reduce((sum, m) => sum + (Number(m.qty) || 1) * (Number(String(m.kw).replace(',', '.')) || 0), 0);
  const ioPlaceholder = (type) => (resolved && resolved.io?.[type] ? `→ ${resolved.io[type]}` : '0');

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start" gap="300">
          <Box minWidth="220px">
            <TextField label={`Cabinet ${index + 1}`} value={cabinet.name} onChange={(value) => set({ name: value })} autoComplete="off" />
          </Box>
          <InlineStack gap="300" blockAlign="end">
            <Box width="150px">
              <Select label="Type" options={KIND_OPTIONS} value={cabinet.kind} onChange={(value) => set({ kind: value })} />
            </Box>
            {cabinet.kind === 'floor' && (
              <Box width="120px">
                <TextField
                  label="Width"
                  type="number"
                  suffix="mm"
                  value={cabinet.widthMm}
                  placeholder={resolved?.widthAuto ? String(resolved.widthMm) : 'auto'}
                  helpText={resolved?.widthAuto ? 'auto' : undefined}
                  onChange={(value) => set({ widthMm: value })}
                  autoComplete="off"
                />
              </Box>
            )}
            <Box width="110px">
              <TextField
                label="Incoming"
                type="number"
                suffix="A"
                value={cabinet.incomingA}
                placeholder={resolved?.incomingAuto ? String(resolved.incomingA) : 'auto'}
                helpText={resolved?.incomingAuto ? 'auto' : undefined}
                onChange={(value) => set({ incomingA: value })}
                autoComplete="off"
              />
            </Box>
            <InlineStack gap="100">
              <Button icon={DuplicateIcon} accessibilityLabel="Duplicate cabinet" onClick={onDuplicate} />
              <Button icon={DeleteIcon} accessibilityLabel="Remove cabinet" tone="critical" disabled={!canRemove} onClick={onRemove} />
            </InlineStack>
          </InlineStack>
        </InlineStack>

        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm">
              PLC &amp; IO
            </Text>
            {resolved && resolved.ioChannels > 0 && (
              <Text as="span" variant="bodySm" tone="subdued">
                Priced as {resolved.ioChannels} channels (incl. reserve, whole modules)
              </Text>
            )}
          </InlineStack>
          <InlineGrid columns={{ xs: 3, md: 6 }} gap="200">
            {Object.entries(IO_LABELS).map(([type, label]) => (
              <TextField key={type} label={label} type="number" min={0} value={cabinet.io[type]} placeholder={ioPlaceholder(type)} onChange={(value) => setIo(type, value)} autoComplete="off" />
            ))}
          </InlineGrid>
          <InlineStack gap="400" blockAlign="end">
            <Checkbox label="Siemens PLC CPU in this cabinet" checked={cabinet.plcCpu} onChange={(value) => set({ plcCpu: value })} />
            <Box width="150px">
              <TextField
                label="IO head stations"
                type="number"
                min={0}
                value={cabinet.ioStations}
                placeholder={resolved ? `auto: ${resolved.ioStations}` : 'auto'}
                onChange={(value) => set({ ioStations: value })}
                autoComplete="off"
              />
            </Box>
            <Box width="150px">
              <TextField
                label="Weighing points"
                type="number"
                min={0}
                value={cabinet.weighingPoints}
                placeholder="0"
                helpText="SIWAREX / load cells"
                onChange={(value) => set({ weighingPoints: value })}
                autoComplete="off"
              />
            </Box>
            <Box width="150px">
              <TextField
                label="Safety relays"
                type="number"
                min={0}
                value={cabinet.safetyRelays}
                placeholder={resolved ? `auto: ${resolved.safetyRelays}` : 'auto'}
                onChange={(value) => set({ safetyRelays: value })}
                autoComplete="off"
              />
            </Box>
          </InlineStack>
        </BlockStack>

        <Divider />

        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm">
              Motors
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {motorCount} motor{motorCount === 1 ? '' : 's'} · {formatNumber(motorKw)} kW
            </Text>
          </InlineStack>
          {cabinet.motors.length > 0 && (
            <BlockStack gap="150">
              <div style={{ display: 'grid', gridTemplateColumns: MOTOR_GRID, gap: 8 }}>
                {['Description', 'Power (kW)', 'Starter', 'Qty', ''].map((h, i) => (
                  <Text key={i} as="span" variant="bodySm" tone="subdued">
                    {h}
                  </Text>
                ))}
              </div>
              {cabinet.motors.map((motor) => (
                <MotorRow key={motor.key} motor={motor} onChange={(next) => setMotor(motor.key, next)} onRemove={() => set({ motors: cabinet.motors.filter((m) => m.key !== motor.key) })} />
              ))}
            </BlockStack>
          )}
          <InlineStack gap="200">
            <Button icon={PlusIcon} onClick={() => addMotor('dol')}>
              Direct (DOL)
            </Button>
            <Button icon={PlusIcon} onClick={() => addMotor('softstarter')}>
              Soft starter
            </Button>
            <Button icon={PlusIcon} onClick={() => addMotor('vfd')}>
              Frequency drive
            </Button>
          </InlineStack>
        </BlockStack>

        {result && (
          <>
            <Divider />
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" variant="headingSm">
                    Estimated price
                  </Text>
                  <Button variant="plain" onClick={() => setShowLines((v) => !v)}>
                    {showLines ? 'Hide calculation' : 'Show calculation'}
                  </Button>
                </InlineStack>
                <Text as="span" variant="headingMd">
                  {formatEur(result.total)}
                </Text>
              </InlineStack>
              <Collapsible open={showLines} id={`lines-${cabinet.key}`}>
                <BlockStack gap="100">
                  {result.lines.map((line) => (
                    <div key={line.key} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12 }}>
                      <Tooltip content={`${line.description} · ${GROUP_LABELS[line.group] || line.group}`}>
                        <Text as="span" variant="bodySm">
                          {line.label}
                        </Text>
                      </Tooltip>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {formatNumber(line.qty)} {line.unit} × {formatEur(line.unitPrice)}
                      </Text>
                      <Text as="span" variant="bodySm" alignment="end">
                        {formatEur(line.amount)}
                      </Text>
                    </div>
                  ))}
                </BlockStack>
              </Collapsible>
              <ComparableList comparables={result.comparables} />
            </BlockStack>
          </>
        )}
      </BlockStack>
    </Card>
  );
}
